/**
 * Tile — service catalogue card (v2, icon-left, Elest.io pattern)
 *
 * WP-SL-V3 additions:
 *   - Body click opens ServiceDetailModal (description, plans, pricing)
 *   - TileCTA continues to handle order/launch/manage actions directly
 *   - Coming-soon and private tiles remain muted + non-interactive
 *
 * brief-20: in the admin catalogue the card carries no commerce CTA — see
 *   convention-admin-catalogue-no-commerce-cta. Gated at this call site so
 *   TileCTA itself stays mode-agnostic.
 *
 * catalogue-card-v2:
 *   - `.dp-card` primitive replaces the old header-strip layout; icon mark
 *     sits left of title/description, footer holds "View details" + deploy meta.
 */

import { useState } from 'react';
import { t } from '../i18n';
import type { TileConfig } from '../config/schema';
import type { Route } from '../shell/Router';
import TileCTA from './TileCTA';
import SubscribeModal from './SubscribeModal';
import ServiceDetailModal from './ServiceDetailModal';
import DeployRolloutModal from './DeployRolloutModal';
import EditDescriptionModal from './EditDescriptionModal';
import PinToggle from './PinToggle';
import { lifecycleBadge } from './catalogBadge';
import type { CartItem } from '../pages/CartPage';

interface TileProps {
  tile: TileConfig;
  /** When true, TileCTA uses POST /bindings flow instead of tile.href */
  useBindingFlow?: boolean;
  onAddToCart?: (item: CartItem) => void;
  /**
   * brief-17-admin-catalogue-tile-actions: true in hub-like modes
   * (vertical-ref, tier-1-hub) — this is the admin's own service catalogue,
   * not a consumer marketplace, so tiles get Admin/Deploy/Edit actions and
   * the lifecycle status badge instead of the plain UAT/Live badge.
   */
  isAdminCatalog?: boolean;
  onNavigate?: (path: Route) => void;
  /** brief-26: pin to the top of the catalogue. Admin catalogue only. */
  pinned?: boolean;
  onTogglePin?: () => void;
}

export default function Tile({
  tile, useBindingFlow = false, onAddToCart, isAdminCatalog = false, onNavigate,
  pinned = false, onTogglePin,
}: TileProps) {
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const isUat = tile.deployment?.phase === 'uat';
  // A private tile carrying the active UAT deployment is actionable for its
  // authorised audience (the server-side ADR-013 predicate already gated it).
  const isMuted = tile.status === 'coming-soon' || (tile.status === 'private' && !isUat);
  /**
   * brief-23: muting is a STOREFRONT signal — opacity 0.66 says "a consumer
   * cannot get this yet". In the admin catalogue the same `private` status is
   * what deriveLifecycle() reports as ONBOARDING, and an onboarding app is
   * exactly the one an operator needs to open, deploy or re-describe: the admin
   * actions were never gated on isMuted, so the card only looked disabled. The
   * badge carries the lifecycle here; the dimming does not belong.
   */
  const dimmed = isMuted && !isAdminCatalog;

  /**
   * convention-admin-catalogue-no-commerce-cta (brief-20, tightened in
   * brief-22): the admin catalogue is the broker's own service surface, not a
   * storefront — the tile carries NO cta at all. `order` and `manage` are
   * commerce; `launch` was kept at first but produced an uneven grid (one card
   * with an Open pill made its whole row taller) for an affordance the operator
   * already has through Admin. Storefront modes keep the full cta path.
   */
  const showCta = !isAdminCatalog;
  // With no cta and no View-details link, the footer would render as an empty
  // bordered strip unless the tile has deploy meta to show.
  const showFoot = !isAdminCatalog || Boolean(tile.deployment);

  const badgeClass = isAdminCatalog
    ? lifecycleBadge(tile).cls
    : isUat
      ? 'dp-badge--uat'
      : tile.status === 'coming-soon'
        ? 'dp-badge--soon'
        : tile.status === 'private'
          ? 'dp-badge--private'
          : 'dp-badge--live';

  const badgeLabel = isAdminCatalog
    ? lifecycleBadge(tile).label
    : isUat
      ? t('tile.badge.uat')
      : tile.status === 'coming-soon'
        ? t('tile.badge.soon')
        : tile.status === 'private'
          ? t('tile.badge.private')
          : t('tile.badge.live');

  /**
   * brief-21: the admin catalogue has no plans-and-pricing detail modal. An
   * operator's entry points are Admin / Deploy / Edit description; a consumer
   * pricing overlay on the broker's own service surface is the storefront
   * leaking in (same reasoning as the commerce CTA, brief-20).
   */
  function openDetail() {
    if (!isMuted && !isAdminCatalog) setShowDetailModal(true);
  }

  return (
    <>
    <article
      className="dp-card dp-card--badged"
      data-state={dimmed ? 'muted' : undefined}
      onClick={isAdminCatalog ? undefined : openDetail}
    >
      <span className={`dp-badge ${badgeClass}`}>{badgeLabel}</span>

      <div className="dp-card__head">
        <div className={`dp-card__mark${dimmed ? ' dp-card__mark--muted' : ''}`} aria-hidden="true">
          {tile.mark}
        </div>
        <div className="dp-card__text">
          <h3 className="dp-card__title">{tile.title}</h3>
          <p className="dp-card__desc">{tile.description}</p>
        </div>
      </div>

      {/* brief-17-admin-catalogue-tile-actions: Admin / Deploy / Edit description —
          admin-scoped actions, only in the meta/vertical broker's own catalogue. */}
      {isAdminCatalog && (
        <div className="dp-card__adminrow" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="dp-btn dp-btn--sm dp-btn--ghost"
            onClick={() => onNavigate?.(`/apps/${tile.id}/operations`)}
          >
            {t('tile.admin')}
          </button>
          <button
            type="button"
            className="dp-btn dp-btn--sm dp-btn--ghost"
            onClick={() => setShowDeployModal(true)}
          >
            {t('tile.deploy.action')}
          </button>
          <button
            type="button"
            className="dp-btn dp-btn--sm dp-btn--ghost"
            onClick={() => setShowEditModal(true)}
          >
            {t('tile.editDescription.action')}
          </button>

          {onTogglePin && (
            <span className="dp-card__pinslot">
              <PinToggle pinned={pinned} title={tile.title} onToggle={onTogglePin} />
            </span>
          )}
        </div>
      )}

      {/* Footer: "View details" link + deployment meta (never its own row) + CTA.
          The link is dropped in the admin catalogue — the admin action row above
          covers what an operator needs, and the card body still opens the detail
          modal on click. Storefront modes keep it: there it's the consumer's
          primary affordance into plans/pricing. */}
      {showFoot && (
      <div className="dp-card__foot">
        <div className="dp-card__footleft">
          {!isAdminCatalog && (
            <button
              type="button"
              className="dp-link"
              disabled={isMuted}
              aria-label={t('serviceDetail.openAriaLabel').replace('{{service}}', tile.title)}
              onClick={(e) => { e.stopPropagation(); openDetail(); }}
            >
              {t('tile.viewDetails')}
            </button>
          )}
          {tile.deployment && (
            <>
              {!isAdminCatalog && (
                <span className="dp-card__sep" aria-hidden="true">·</span>
              )}
              <p className="dp-card__meta">
                {t('tile.deployment')
                  .replace('{{no}}', String(tile.deployment.deployNo))
                  .replace('{{date}}', tile.deployment.deployDate)}
              </p>
            </>
          )}
        </div>

        {/* stopPropagation so body-click doesn't double-trigger */}
        {showCta && (
          <div className="dp-card__cta" onClick={(e) => e.stopPropagation()}>
            <TileCTA
              tile={tile}
              useBindingFlow={useBindingFlow}
              onOrder={() => setShowSubscribeModal(true)}
            />
          </div>
        )}
      </div>
      )}
    </article>

    {showDetailModal && (
      <ServiceDetailModal
        tile={tile}
        isAdminCatalog={isAdminCatalog}
        onClose={() => setShowDetailModal(false)}
        onOrder={() => {
          setShowDetailModal(false);
          setShowSubscribeModal(true);
        }}
        onAddToCart={onAddToCart}
      />
    )}

    {showSubscribeModal && (
      <SubscribeModal tile={tile} onClose={() => setShowSubscribeModal(false)} />
    )}

    {showDeployModal && (
      <DeployRolloutModal tile={tile} onClose={() => setShowDeployModal(false)} />
    )}

    {showEditModal && (
      <EditDescriptionModal tile={tile} onClose={() => setShowEditModal(false)} />
    )}
    </>
  );
}
